/** Admin Rewards — API mappers, form builders, and listing config. */

import dayjs from "dayjs"
import { getUserFullName } from "../../../helpers/userDisplay"

export const REWARD_KPI_TONES = {
  xp: "lightblue-custom",
  rewards: "lightteal",
  claims: "red",
  weightage: "peach",
}

/** Static KPI shell — values filled from GET /api/v1/admin/rewards/stats. */
export const REWARD_KPI = [
  {
    key: "xp",
    title: "Total XP Awarded",
    value: "--",
    tone: REWARD_KPI_TONES.xp,
  },
  {
    key: "rewards",
    title: "Total Treasures",
    value: "--",
    tone: REWARD_KPI_TONES.rewards,
  },
  {
    key: "claims",
    title: "Total Claims",
    value: "--",
    tone: REWARD_KPI_TONES.claims,
  },
]

/** City Admin Rewards — extra KPI beyond platform stats. */
export const CITY_PORTAL_REWARD_KPI = [
  {
    key: "weightage",
    title: "Activity Weightage",
    value: "--",
    tone: REWARD_KPI_TONES.weightage,
  },
]

export const REWARD_STATUS_OPTIONS = [
  { value: "active", label: "Active", showDot: true, dotTone: "active" },
  { value: "draft", label: "Draft", showDot: true, dotTone: "draft" },
]

export const REWARD_STATUS_FILTER_OPTIONS = REWARD_STATUS_OPTIONS.map(
  option => ({
    value: option.value,
    label: option.label,
  }),
)

export const CLAIM_STATUS_OPTIONS = [
  { value: "pending", label: "Pending", showDot: true, dotTone: "pending" },
  { value: "approved", label: "Approved", showDot: true, dotTone: "approved" },
  { value: "rejected", label: "Rejected", showDot: true, dotTone: "rejected" },
]

export const CLAIM_STATUS_FILTER_OPTIONS = CLAIM_STATUS_OPTIONS.map(option => ({
  value: option.value,
  label: option.label,
}))

export const CLAIM_SORT_BY_OPTIONS = [
  { value: "claimed_at", label: "Claimed At" },
  { value: "claimed_at_level", label: "Claimed At Level" },
]

export const CLAIM_SORT_DIRECTION_OPTIONS = [
  { value: "desc", label: "Descending" },
  { value: "asc", label: "Ascending" },
]

export const DEFAULT_CLAIM_SORT_BY = "claimed_at"
export const DEFAULT_CLAIM_SORT_DIRECTION = "desc"

export const REWARD_MAX_MEDIAS = 2
export const REWARD_MAX_MEDIA_BYTES = 5 * 1024 * 1024
export const REWARD_MEDIA_ACCEPT =
  "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,application/pdf,text/csv,.pdf,.csv"

export const REWARD_ACCEPTED_MEDIA_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "text/csv",
  "application/csv",
  "text/comma-separated-values",
]

const REWARD_ACCEPTED_MEDIA_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".svg",
  ".pdf",
  ".csv",
]

export const isAcceptedRewardMediaFile = file => {
  const mime = String(file?.type || "").toLowerCase()
  if (REWARD_ACCEPTED_MEDIA_TYPES.includes(mime)) return true
  const name = String(file?.name || "").toLowerCase()
  return REWARD_ACCEPTED_MEDIA_EXTENSIONS.some(ext => name.endsWith(ext))
}

export const isRewardMediaImage = ({ mimeType, name, url, file } = {}) => {
  const mime = String(mimeType || file?.type || "").toLowerCase()
  if (mime.startsWith("image/")) return true
  const fileName = String(name || file?.name || url || "").toLowerCase()
  return /\.(png|jpe?g|webp|svg)(\?|$)/i.test(fileName)
}

export const getRewardMediaTypeLabel = ({ mimeType, name, url, file } = {}) => {
  const mime = String(mimeType || file?.type || "").toLowerCase()
  const fileName = String(name || file?.name || url || "").toLowerCase()
  if (mime === "application/pdf" || /\.pdf(\?|$)/i.test(fileName)) return "PDF"
  if (
    mime.includes("csv") ||
    mime === "text/comma-separated-values" ||
    /\.csv(\?|$)/i.test(fileName)
  ) {
    return "CSV"
  }
  return "FILE"
}

export const formatRewardStat = value => {
  const num = Number(value)
  if (!Number.isFinite(num)) return "--"
  return num.toLocaleString("en-US")
}

export const mapRewardDetailKpi = (reward = {}, claimsTotal = null) => [
  {
    key: "claims",
    title: "Total Claims",
    value:
      claimsTotal == null
        ? formatRewardStat(reward.claimsCount)
        : formatRewardStat(claimsTotal),
    tone: REWARD_KPI_TONES.claims,
  },
  {
    key: "quantity",
    title: "Total Quantity",
    value: formatRewardStat(reward.totalQuantity),
    tone: REWARD_KPI_TONES.rewards,
  },
  {
    key: "left",
    title: "Quantity Left",
    value: formatRewardStat(reward.quantityLeft),
    tone: REWARD_KPI_TONES.xp,
  },
]

export const mapRewardsStatsKpi = (stats = {}, extraCards = []) => {
  const base = [
    {
      key: "xp",
      title: "Total XP Awarded",
      value: formatRewardStat(stats.total_xp_awarded),
      tone: REWARD_KPI_TONES.xp,
    },
    {
      key: "rewards",
      title: "Total Treasures",
      value: formatRewardStat(stats.total_rewards),
      tone: REWARD_KPI_TONES.rewards,
    },
    {
      key: "claims",
      title: "Total Claims",
      value: formatRewardStat(stats.total_claims),
      tone: REWARD_KPI_TONES.claims,
    },
  ]

  const extras = (Array.isArray(extraCards) ? extraCards : []).filter(
    card => card?.key && !["xp", "rewards", "claims"].includes(card.key),
  )

  return [...base, ...extras]
}

const pickCityIds = item => {
  if (Array.isArray(item?.city_ids) && item.city_ids.length > 0) {
    return item.city_ids.filter(Boolean).map(String)
  }
  if (Array.isArray(item?.cities) && item.cities.length > 0) {
    return item.cities.map(city => city?.id).filter(Boolean).map(String)
  }
  return []
}

/** Coerce API 1/0, "1"/"0", true/false → boolean. */
export const toRewardFlag = value =>
  value === true || value === 1 || value === "1"

const formatRewardCityName = city => {
  if (!city) return ""
  if (typeof city === "string") return city.trim()
  return String(city.city || city.name || "").trim()
}

/** Display label: "All cities" or comma-separated city names. */
export const formatRewardCitiesLabel = (item = {}) => {
  if (toRewardFlag(item?.is_all_cities ?? item?.isAllCities)) {
    return "All cities"
  }
  const cities = Array.isArray(item?.cities)
    ? item.cities
    : Array.isArray(item?.raw?.cities)
      ? item.raw.cities
      : []
  const labels = cities.map(formatRewardCityName).filter(Boolean)
  if (labels.length) return labels.join(", ")
  return "--"
}

const mapMedias = medias =>
  (Array.isArray(medias) ? medias : []).map(media => ({
    id: media.id,
    url: media.media_url || media.media_thumbnail_url || "",
    thumbnailUrl: media.media_thumbnail_url || media.media_url || "",
    mimeType: media.mime_type || "",
    name: media.media || "Reward media",
  }))

export const getRewardInitials = name => {
  const cleaned = String(name || "")
    .trim()
    .replace(/^--+/, "")
  if (!cleaned) return "?"
  return cleaned
    .split(/\s+/)
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

/**
 * Map GET /api/v1/admin/rewards item → table row.
 */
export const mapRewardRow = item => {
  const totalQuantity = Number(item.total_quantity)
  const quantityLeft = Number(item.quantity_left)
  const claimsCount = Number(item.claims_count)
  const medias = mapMedias(item.medias)
  const status = String(item.status || "")
    .trim()
    .toLowerCase()
  const name = item.name || "--"
  const isAllCities = toRewardFlag(item.is_all_cities)

  return {
    id: item.id,
    name,
    initials: getRewardInitials(name),
    type: item.type || "",
    typeLabel: item.type || "--",
    description: item.description || "",
    status: status === "draft" ? "draft" : status || "active",
    isAllCities,
    isOneTime: toRewardFlag(item.is_one_time),
    totalQuantity: Number.isFinite(totalQuantity) ? totalQuantity : 0,
    quantityLeft: Number.isFinite(quantityLeft) ? quantityLeft : 0,
    claimsCount: Number.isFinite(claimsCount) ? claimsCount : 0,
    canDelete: Number.isFinite(claimsCount) ? claimsCount === 0 : true,
    quantityLabel: Number.isFinite(totalQuantity)
      ? `${Number.isFinite(quantityLeft) ? quantityLeft : 0} / ${totalQuantity}`
      : "--",
    cityIds: pickCityIds(item),
    citiesLabel: formatRewardCitiesLabel(item),
    medias,
    thumbnail: medias[0]?.thumbnailUrl || medias[0]?.url || "",
    createdAt: item.created_at || "",
    creator: item.creator || null,
    raw: item,
  }
}

/**
 * Map GET /api/v1/admin/rewards/:id → edit form model.
 */
export const mapRewardDetail = item => {
  const row = mapRewardRow(item || {})
  return {
    ...row,
    name: item?.name || "",
    type: item?.type || "",
    description: item?.description || "",
  }
}

const getUserDisplayName = user => {
  if (!user) return "--"
  return getUserFullName(user, { fallback: "--" })
}

const getUserInitials = user => {
  const name = getUserDisplayName(user)
  if (!name || name === "--") return "?"
  return name
    .split(" ")
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

const formatClaimDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "--"
}

/**
 * Map GET /api/v1/admin/reward-claims item → table row.
 */
export const mapRewardClaimRow = item => {
  const user = item.user || {}
  const status = String(item.status || "")
    .trim()
    .toLowerCase()
  const level = Number(item.claimed_at_level)
  const totalXp = Number(user.total_xp)

  return {
    id: item.id,
    rewardId: item.reward_id || item.reward?.id || "",
    userId: item.user_id || user.id || "",
    name: getUserDisplayName(user),
    initials: getUserInitials(user),
    email: user.email || "--",
    phone: user.phone_number || "",
    image:
      user.profile_media?.thumbnail ||
      user.profile_media?.image ||
      user.thumbnail ||
      user.image ||
      "",
    claimedAtLevel: Number.isFinite(level) ? level : null,
    levelLabel: Number.isFinite(level) ? `Level ${level}` : "--",
    totalXp: Number.isFinite(totalXp) ? totalXp : null,
    xpLabel: Number.isFinite(totalXp)
      ? `${totalXp.toLocaleString("en-US")} XP`
      : "--",
    status: status || "pending",
    claimedAt: item.claimed_at || item.created_at || "",
    claimedAtLabel: formatClaimDate(item.claimed_at || item.created_at),
    approvedAt: item.approved_at || "",
    rejectedAt: item.rejected_at || "",
    reason: item.reason || "",
    medias: Array.isArray(item.medias) ? item.medias : [],
    raw: item,
  }
}

export const mapCitySelectOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map(item => {
      const value = String(item?.id || "").trim()
      if (!value) return null
      return {
        value,
        label:
          [item.city || item.name, item.state].filter(Boolean).join(", ") ||
          value,
      }
    })
    .filter(Boolean)

/** Map GET /api/v1/admin/reward-types/dropdown result (string[]) → select options. */
export const mapRewardTypeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map(item => {
      if (typeof item === "string") {
        const value = item.trim()
        return value ? { value, label: value } : null
      }
      const value = String(item?.value || item?.name || item?.type || "").trim()
      if (!value) return null
      return {
        value,
        label: String(item?.label || item?.name || item?.type || value).trim(),
      }
    })
    .filter(Boolean)

const appendArray = (formData, key, values = []) => {
  ;(Array.isArray(values) ? values : []).forEach(value => {
    if (value == null || value === "") return
    formData.append(`${key}[]`, String(value))
  })
}

/**
 * Build multipart FormData for create/update admin reward.
 * Create: POST /api/v1/admin/rewards
 * Update: POST /api/v1/admin/rewards/:id (+ remove_media_ids[])
 * is_all_cities=1 → no city_ids; is_all_cities=0 → city_ids[].
 */
export const buildRewardFormData = ({ values, isEdit = false } = {}) => {
  const formData = new FormData()
  const status =
    String(values?.status || "active").trim().toLowerCase() === "draft"
      ? "draft"
      : "active"
  const isAllCities = Boolean(values?.isAllCities)
  const cityIds = (
    Array.isArray(values?.cityIds) ? values.cityIds : []
  )
    .map(id => String(id || "").trim())
    .filter(Boolean)

  formData.append("name", String(values?.name || "").trim())
  formData.append("type", String(values?.type || "").trim())
  formData.append("status", status)
  formData.append("is_all_cities", isAllCities ? "1" : "0")
  if (!isAllCities) {
    appendArray(formData, "city_ids", cityIds)
  }
  formData.append("is_one_time", values?.isOneTime ? "1" : "0")
  formData.append(
    "total_quantity",
    values?.isOneTime
      ? "1"
      : String(
          Number.isFinite(Number(values?.totalQuantity))
            ? Math.max(0, Math.floor(Number(values.totalQuantity)))
            : 0,
        ),
  )
  formData.append("description", String(values?.description || "").trim())

  const previews = Array.isArray(values?.previews) ? values.previews : []
  const newFiles = previews
    .map(item => item?.file)
    .filter(file => file instanceof File)
    .slice(0, REWARD_MAX_MEDIAS)

  newFiles.forEach(file => {
    formData.append("medias[]", file)
  })

  if (isEdit) {
    const keptExistingIds = new Set(
      previews
        .filter(item => item?.isExisting && item?.mediaId)
        .map(item => String(item.mediaId)),
    )
    const initialIds = (
      Array.isArray(values?.initialMediaIds) ? values.initialMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)
    const explicitRemoved = (
      Array.isArray(values?.removedMediaIds) ? values.removedMediaIds : []
    )
      .map(id => String(id || "").trim())
      .filter(Boolean)

    const removedIds = [
      ...explicitRemoved,
      ...initialIds.filter(id => !keptExistingIds.has(id)),
    ]

    appendArray(formData, "remove_media_ids", [...new Set(removedIds)])
  }

  return formData
}
